import { GraphQLError } from "graphql";
import isEmpty from "is-empty";
import { Moment } from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CriteriaValueInput, Service } from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import InputPassword from "@components/_Layout/Controls/InputPassword";
import InputPasswordConfirm from "@components/_Layout/Controls/InputPasswordConfirm";
import InputPlacesAutocomplete from "@components/_Layout/Controls/InputPlacesAutocomplete";
import InputText from "@components/_Layout/Controls/InputText";
import RadioCivility from "@components/_Layout/Controls/RadioCivility";
import SDPicker from "@components/_Layout/Controls/SDPicker";
import SelectCivility from "@components/_Layout/Controls/SelectCivility";
import SelectCountry from "@components/_Layout/Controls/SelectCountry";
import CustomerCriterias from "@components/Criterias/CustomerCriterias";
import { useAuth, useSession } from "@hooks";
import { focusElementById } from "@utils/dom";
import { convertSuggestionToStreetname } from "@utils/tools";
import { sendDataLayer } from "@utils/tracking/gtm";

type RegisterProps = {
  onRegisterSuccess?: (service: Service | null) => void;
};

const languagesVarsCodes = [
  "LANG_neobooking_password", //Mot de passe
  "LANG_neobooking_confirm_password", //Confirmer le mot de passe
  "LANG_neobooking_civility", //Civilité
  "LANG_neobooking_lastname", //Nom
  "LANG_neobooking_firstname", //Prénom
  "LANG_neobooking_city", //Ville
  "LANG_neobooking_adr_street", //N° et voie
  "LANG_neobooking_adr_zipcode", //Code postal
  "LANG_neobooking_adr_tel_1", //Téléphone 1
  "LANG_neobooking_email_address", //Adresse email
  "LANG_neobooking_error_while_register", //Erreur lors de l'inscription
  "LANG_neobooking_register_in_progess", //Inscription en cours
  "LANG_neobooking_account_successfully_created", //Votre compte a bien été créé !
  "LANG_neobooking_next", //Suivant
];

const Register: React.FC<RegisterProps> = ({ onRegisterSuccess }) => {
  /* Hooks */
  const { sessionId, getLanguageVarValue } = useSession();
  const client = useApolloClient();
  const { register } = useAuth();

  /* State */
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordConfirm, setPasswordConfirm] = useState<string>("");

  const [lastname, setLastname] = useState<string>("");
  const [firstname, setFirstname] = useState<string>("");

  const [birthdate, setBirthdate] = useState<Moment | null>(null);
  const [language, setLanguage] = useState<string | undefined>(undefined);
  const [civility, setCivility] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<string | undefined>(undefined);

  const [streetName, setStreetName] = useState<string>("");
  const [postalLocality, setPostalLocality] = useState<string>("");
  const [addressComplementary1, setAddressComplementary1] =
    useState<string>("");
  const [addressComplementary2, setAddressComplementary2] =
    useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [phone1, setPhone1] = useState<string>("");
  const [phone2, setPhone2] = useState<string>("");
  const [fax, setFax] = useState<string>("");
  const [mail, setMail] = useState<string>("");

  /* Customer Criterias State */
  const [customerCriteriaValueInput, setCustomerCriteriaValueInput] = useState<
    CriteriaValueInput[]
  >([]);

  const [selectedSuggestion, setSelectedSuggestion] =
    useState<any | null>(null);

  /* Submit / Error State */
  const [responseError, setResponseError] = useState<GraphQLError>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (selectedSuggestion && selectedSuggestion !== null) {
      let streetname = convertSuggestionToStreetname(selectedSuggestion);
      let zipcode = "";
      let country = "";
      let city = "";

      if (!isEmpty(selectedSuggestion.context)) {
        const contexts = selectedSuggestion.context as any[];

        contexts.forEach((context) => {
          if (
            !isEmpty(context) &&
            (context.id as string).includes("postcode")
          ) {
            zipcode = context.text;
          }

          if (!isEmpty(context) && (context.id as string).includes("country")) {
            country = context.short_code.toUpperCase();
          }

          if (!isEmpty(context) && (context.id as string).includes("place")) {
            city = context.text;
          }
        });
      }

      setCountry(country);
      setCity(city);
      setStreetName(streetname);
      setZipCode(zipcode);
    }
  }, [selectedSuggestion]);

  const handleSubmitRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!birthdate) {
      toast.error(
        getLanguageVarValue("LANG_neobooking_please_select_birthdate")
      );
      focusElementById("sdr_SingleDatePicker");
      return;
    }

    if (firstname.trim().length <= 1 || lastname.trim().length <= 1) {
      toast.error(
        getLanguageVarValue(
          "LANG_neobooking_register_invalid_lastname_firstname"
        )
      );
      return;
    }

    if (sessionId) {
      const toastRegister = toast.loading(
        `${getLanguageVarValue("LANG_neobooking_register_in_progess")}...`
      );
      setIsSubmitting(true);
      setResponseError(undefined);

      const criteriaValueInput: CriteriaValueInput[] = Object.entries(
        customerCriteriaValueInput
      ).map(([key, val]) => {
        return {
          code: key,
          value: val,
        };
      }) as any;

      register(
        mail,
        password,
        passwordConfirm,
        civility ? civility : "",
        firstname,
        lastname,
        birthdate ? birthdate.toDate() : new Date(),
        language ? language : "FR",
        streetName,
        postalLocality,
        addressComplementary1,
        addressComplementary2,
        zipCode,
        city,
        country ? country : "FR",
        phone1,
        phone2,
        fax,
        mail,
        criteriaValueInput,
        (service, err) => {
          if (err) {
            toast.error(err.message, {
              id: toastRegister,
            });
            setResponseError(err);
          } else {
            toast.success(
              getLanguageVarValue(
                "LANG_neobooking_account_successfully_created"
              ),
              {
                id: toastRegister,
              }
            );

            sendDataLayer({
              event: "creation_compte_ok",
            });

            onRegisterSuccess && onRegisterSuccess(service);
          }
          setIsSubmitting(false);
        }
      );
    }
  };

  return (
    <>
      <div className="tw-flex">
        <div className="tw-w-full">
          <form
            onSubmit={handleSubmitRegister}
            onChange={() => setResponseError(undefined)}
          >
            <div className="tw-my-4">
              <div className="tw-grid tw-grid-cols-12 tw-gap-6">
                <div className="tw-col-span-12">
                  <div className="tw-block sm:tw-hidden">
                    <SelectCivility
                      required
                      value={civility}
                      onChange={(e) => setCivility(e.target.value)}
                      additionalClassname="tw-w-full"
                    />
                  </div>
                  <div className="tw-hidden sm:tw-block">
                    <div className="tw-mb-2">
                      {getLanguageVarValue("LANG_neobooking_civility")}*
                    </div>
                    <RadioCivility
                      required
                      value={civility}
                      onChange={(e) => setCivility(e.target.value)}
                    />
                  </div>
                </div>
                <div className="tw-col-span-12">
                  <InputText
                    required
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_lastname") + "*"
                    }
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                    additionalClassname="tw-w-full"
                    type="text"
                  />
                </div>
                <div className="tw-col-span-12">
                  <InputText
                    required
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_firstname") + "*"
                    }
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                    additionalClassname="tw-w-full"
                    type="text"
                  />
                </div>
                <div className="tw-col-span-12">
                  <SDPicker
                    selectedSingleDate={birthdate}
                    setSelectedSingleDate={setBirthdate}
                    isRequired={true}
                  />
                </div>
                <div className="tw-col-span-12">
                  <InputText
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_adr_tel_1") + "*"
                    }
                    value={phone1}
                    onChange={(e) => setPhone1(e.target.value)}
                    additionalClassname="tw-w-full"
                    type="text"
                  />
                </div>
                <div className="tw-col-span-12">
                  {isEmpty(process.env.REACT_APP_MAPBOX_GEOCODING_API_KEY) ? (
                    <InputText
                      placeholder={
                        getLanguageVarValue("LANG_neobooking_adr_street") + "*"
                      }
                      required
                      value={streetName}
                      onChange={(e) => setStreetName(e.target.value)}
                      additionalClassname="tw-w-full"
                      type="text"
                    />
                  ) : (
                    <InputPlacesAutocomplete
                      defaultValue={streetName}
                      setSelectedSuggestion={setSelectedSuggestion}
                    />
                  )}
                </div>
                <div className="tw-col-span-12">
                  <div className="tw-grid tw-items-end tw-grid-cols-12 tw-gap-4">
                    <div className="tw-col-span-6 md:tw-col-span-3">
                      <InputText
                        required
                        placeholder={
                          getLanguageVarValue("LANG_neobooking_adr_zipcode") +
                          "*"
                        }
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        additionalClassname="tw-w-full"
                        type="text"
                      />
                    </div>
                    <div className="tw-col-span-6 md:tw-col-span-5">
                      <InputText
                        required
                        placeholder={
                          getLanguageVarValue("LANG_neobooking_city") + "*"
                        }
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        additionalClassname="tw-w-full"
                        type="text"
                      />
                    </div>
                    <div className="tw-col-span-12 md:tw-col-span-4">
                      <SelectCountry
                        required
                        value={country ?? "FR"}
                        onChange={(e) => setCountry(e.target.value)}
                        additionalClassname="tw-w-full"
                      />
                    </div>
                  </div>
                </div>
                <div className="tw-col-span-12">
                  <InputText
                    required
                    value={mail}
                    onChange={(e) => setMail(e.target.value)}
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_email_address") + "*"
                    }
                    additionalClassname="tw-w-full"
                    type="mail"
                  />
                </div>
                <div className="tw-col-span-12">
                  <InputPassword
                    minLength={8}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    additionalClassname="tw-w-full"
                    showStrength={true}
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_password") + "*"
                    }
                    showViewPassword={true}
                  />
                </div>
                <div className="tw-col-span-12">
                  <InputPasswordConfirm
                    minLength={8}
                    required
                    additionalClassname="tw-w-full"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    showConfirmation={true}
                    placeholder={
                      getLanguageVarValue("LANG_neobooking_confirm_password") +
                      "*"
                    }
                    passwordToCheck={password}
                  />
                </div>
              </div>
            </div>
            {responseError && (
              <>
                <div className="tw-mb-4">
                  <Alert
                    dismissable={false}
                    title={`${getLanguageVarValue(
                      "LANG_neobooking_error_while_register"
                    )}`}
                    variant="danger"
                  >
                    {responseError.message}
                  </Alert>
                </div>
              </>
            )}

            <div className="tw-my-2">
              <CustomerCriterias
                criteriaCodes={["NEWS", "SMS"]}
                customerCriteriaValueInput={customerCriteriaValueInput}
                setCustomerCriteriaValueInput={setCustomerCriteriaValueInput}
              />
            </div>
            <div className="tw-flex tw-justify-end ">
              <Button variant="primary" shape="pills" isLoading={isSubmitting} type="submit" additionalClassname="tw-w-1/6">
                <p className="tw-font-bold">{getLanguageVarValue("LANG_neobooking_next")}</p>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

Register.propTypes = {
  //
};

export default Register;
